import React, { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Spin } from 'antd';
import { useQuery } from '@apollo/client';
import moment from 'moment';

import {
  GENERATE_INVOICE_REPORT_MUTATION,
  GET_DATE_INVOICE_QUERY,
} from '../../app/graphql/invoice';
import { useFlashMessageContext } from '../../components/dialogs/FlashMessageProvider';
import InvoiceContainer from './InvoiceContainer';
import InvoiceStatementResultsDisplay from './InvoiceStatementResultsDisplay';
import { extractGraphqlEntity } from '../../common/utils/graphqlUtils';
import { toServerDateOptional } from '../../common/utils/dateUtils';
import { useEmailProcessingDialog } from '../useEmailProcessingDialog';
import { useInvoiceStyles } from './invoice.styles';

export default function DateInvoiceResultsDisplay() {
  const { openDialog, dialog } = useEmailProcessingDialog({
    documentType: 'Invoice List',
    paragraph2Id: 'emailProcessingDialog.paragraph2.withLink',
  });
  const { errorMessage } = useFlashMessageContext();
  const history = useHistory();
  const location = useLocation();
  const { state: dateSearchParams } = location || {};
  const accountNumber = dateSearchParams?.id?.number;
  const dateRangeStart = dateSearchParams?.rangeStart;
  const dateRangeEnd = dateSearchParams?.rangeEnd;
  const { styles } = useInvoiceStyles();

  const {
    data: dateInvoiceQueryResultData,
    loading: dateInvoiceLoading,
    error,
  } = useQuery(GET_DATE_INVOICE_QUERY, {
    variables: {
      filter: {
        id: accountNumber,
        rangeStart: dateRangeStart,
        rangeEnd: dateRangeEnd,
      },
    },
    skip: !dateSearchParams,
    errorPolicy: 'all',
  });
  const dateInvoiceData = dateInvoiceQueryResultData?.getDateInvoice?.data;
  const hasData = dateInvoiceData?.length > 0;
  const invoiceCount = dateInvoiceData?.[0]?.invoiceNumber?.length ?? 0;

  const actionButtons = [
    {
      labelId: 'invoice.download',
      icon: 'download',
      mutationProps: {
        mutation: GENERATE_INVOICE_REPORT_MUTATION,
        mutationOptions: {
          onCompleted: data => {
            const { status } = extractGraphqlEntity(data);
            if (status !== 'SUBMITTED') {
              errorMessage({ contentId: 'invoice.emailList.error' });
            } else {
              openDialog();
            }
          },
          onError: () => {
            errorMessage({ contentId: 'invoice.emailList.error' });
          },
        },
        getMutationArgs: () => {
          const rangeStartMoment = moment(dateRangeStart, 'YYYY/MM/DD');
          const rangeEndMoment = moment(dateRangeEnd, 'YYYY/MM/DD');

          return {
            variables: {
              accountNumber,
              reportName: 'INV.LIST.EMAIL',
              startDate: toServerDateOptional(rangeStartMoment),
              endDate: toServerDateOptional(rangeEndMoment),
            },
          };
        },
      },
    },
  ];

  const handleBack = useCallback(() => {
    history.goBack();
  }, [history]);

  useEffect(() => {
    if (error) {
      errorMessage({ contentId: 'error.appError' });
      handleBack();
    } else if (!dateInvoiceLoading && dateSearchParams && !hasData) {
      errorMessage({ contentId: 'invoice.noInvoicesFound' });
      handleBack();
    }
  }, [
    error,
    dateInvoiceLoading,
    dateSearchParams,
    hasData,
    errorMessage,
    handleBack,
  ]);

  if (dateInvoiceLoading || !dateSearchParams) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <InvoiceContainer
        handleBack={handleBack}
        titleCount={invoiceCount}
        titleId="invoice.list"
        actionButtons={actionButtons}
      >
        <InvoiceStatementResultsDisplay
          accountName={dateSearchParams?.id?.name}
          accountNumber={accountNumber}
          data={dateInvoiceData}
          rangeEnd={dateRangeEnd}
          rangeStart={dateRangeStart}
        />
      </InvoiceContainer>
      {dialog}
    </>
  );
}
